import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { api } from './api';

interface Settings {
    telegram_enabled: boolean; telegram_bot_token: string; telegram_chat_id: string;
    email_enabled: boolean; smtp_host: string; smtp_port: number; smtp_user: string;
    smtp_password: string; smtp_from: string; smtp_to: string; smtp_tls: boolean;
    webhook_enabled: boolean; webhook_url: string;
    cpu_threshold: number; ram_threshold: number;
    disk_warning: number; disk_critical: number;
    down_minutes: number; repeat_minutes: number;
    alert_on_down: boolean; alert_on_cpu: boolean;
    alert_on_ram: boolean; alert_on_disk: boolean;
}

export default function SettingsPage() {
    const [st, setSt]           = useState<Settings | null>(null);
    const [saving, setSaving]   = useState(false);
    const [saved, setSaved]     = useState(false);
    const [error, setError]     = useState('');
    const [testing, setTesting] = useState<string>('');

    useEffect(() => {
        api.get<Settings>('/alerts/settings').then(setSt).catch(e => setError(e.message));
    }, []);

    const upd = <K extends keyof Settings>(key: K, value: Settings[K]) => {
        setSt(prev => prev ? { ...prev, [key]: value } : prev);
        setSaved(false);
    };

    const save = async () => {
        if (!st) return;
        setSaving(true); setError('');
        try {
            const updated = await api.put<Settings>('/alerts/settings', st);
            setSt(updated); setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const test = async (channel: string) => {
        setTesting(channel); setError('');
        try {
            await api.post(`/alerts/test/${channel}`, {});
            alert(`✅ Тестовое сообщение отправлено (${channel})`);
        } catch (e: any) {
            alert(`❌ Ошибка: ${e.message}`);
        } finally { setTesting(''); }
    };

    if (!st) return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content">
                <div className="loading-screen">{error || 'Загрузка...'}</div>
            </main>
        </div>
    );

    return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content">
                <header className="page-header">
                    <h1>Настройки оповещений</h1>
                    <div className="header-actions">
                        {saved && <span className="saved-hint">✓ Сохранено</span>}
                        <button className="btn-primary" style={{ maxWidth: 160 }}
                                onClick={save} disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                    </div>
                </header>

                {error && <div className="error-message">{error}</div>}

                {/* ══ Telegram ══ */}
                <div className="settings-card">
                    <div className="settings-card-header">
                        <h2>📨 Telegram</h2>
                        <label className="toggle">
                            <input type="checkbox" checked={st.telegram_enabled}
                                   onChange={e => upd('telegram_enabled', e.target.checked)} />
                            <span>Включено</span>
                        </label>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Bot Token</label>
                            <input type="password" value={st.telegram_bot_token}
                                   onChange={e => upd('telegram_bot_token', e.target.value)}
                                   placeholder="123456:ABC-DEF..." />
                        </div>
                        <div className="form-group">
                            <label>Chat ID</label>
                            <input type="text" value={st.telegram_chat_id}
                                   onChange={e => upd('telegram_chat_id', e.target.value)}
                                   placeholder="-1001234567890" />
                        </div>
                    </div>
                    <button className="btn-secondary" onClick={() => test('telegram')}
                            disabled={testing === 'telegram'}>
                        {testing === 'telegram' ? 'Отправка...' : 'Отправить тест'}
                    </button>
                </div>

                {/* ══ Email ══ */}
                <div className="settings-card">
                    <div className="settings-card-header">
                        <h2>✉️ Email</h2>
                        <label className="toggle">
                            <input type="checkbox" checked={st.email_enabled}
                                   onChange={e => upd('email_enabled', e.target.checked)} />
                            <span>Включено</span>
                        </label>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>SMTP сервер</label>
                            <input type="text" value={st.smtp_host}
                                   onChange={e => upd('smtp_host', e.target.value)}
                                   placeholder="smtp.gmail.com" />
                        </div>
                        <div className="form-group" style={{ maxWidth: 140 }}>
                            <label>Порт</label>
                            <input type="number" value={st.smtp_port}
                                   onChange={e => upd('smtp_port', Number(e.target.value))} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Пользователь</label>
                            <input type="text" value={st.smtp_user}
                                   onChange={e => upd('smtp_user', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Пароль</label>
                            <input type="password" value={st.smtp_password}
                                   onChange={e => upd('smtp_password', e.target.value)} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>От кого</label>
                            <input type="text" value={st.smtp_from}
                                   onChange={e => upd('smtp_from', e.target.value)}
                                   placeholder="monitor@ivsss.ru" />
                        </div>
                        <div className="form-group">
                            <label>Кому</label>
                            <input type="text" value={st.smtp_to}
                                   onChange={e => upd('smtp_to', e.target.value)}
                                   placeholder="admin@ivsss.ru" />
                        </div>
                    </div>
                    <label className="toggle" style={{ marginBottom: 16 }}>
                        <input type="checkbox" checked={st.smtp_tls}
                               onChange={e => upd('smtp_tls', e.target.checked)} />
                        <span>Использовать STARTTLS (порт 587)</span>
                    </label>
                    <button className="btn-secondary" onClick={() => test('email')}
                            disabled={testing === 'email'}>
                        {testing === 'email' ? 'Отправка...' : 'Отправить тест'}
                    </button>
                </div>

                {/* ══ Webhook ══ */}
                <div className="settings-card">
                    <div className="settings-card-header">
                        <h2>🔗 Webhook</h2>
                        <label className="toggle">
                            <input type="checkbox" checked={st.webhook_enabled}
                                   onChange={e => upd('webhook_enabled', e.target.checked)} />
                            <span>Включено</span>
                        </label>
                    </div>
                    <div className="form-group">
                        <label>URL</label>
                        <input type="text" value={st.webhook_url}
                               onChange={e => upd('webhook_url', e.target.value)}
                               placeholder="https://hooks.slack.com/services/..." />
                    </div>
                    <button className="btn-secondary" onClick={() => test('webhook')}
                            disabled={testing === 'webhook'}>
                        {testing === 'webhook' ? 'Отправка...' : 'Отправить тест'}
                    </button>
                </div>

                {/* ══ Пороги ══ */}
                <div className="settings-card">
                    <div className="settings-card-header"><h2>🎚️ Пороги и правила</h2></div>

                    <div className="checkbox-list">
                        <label className="toggle">
                            <input type="checkbox" checked={st.alert_on_down}
                                   onChange={e => upd('alert_on_down', e.target.checked)} />
                            <span>Сервер недоступен</span>
                        </label>
                        <label className="toggle">
                            <input type="checkbox" checked={st.alert_on_cpu}
                                   onChange={e => upd('alert_on_cpu', e.target.checked)} />
                            <span>Высокая загрузка CPU</span>
                        </label>
                        <label className="toggle">
                            <input type="checkbox" checked={st.alert_on_ram}
                                   onChange={e => upd('alert_on_ram', e.target.checked)} />
                            <span>Высокое использование RAM</span>
                        </label>
                        <label className="toggle">
                            <input type="checkbox" checked={st.alert_on_disk}
                                   onChange={e => upd('alert_on_disk', e.target.checked)} />
                            <span>Заполнение диска</span>
                        </label>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>CPU, %</label>
                            <input type="number" value={st.cpu_threshold}
                                   onChange={e => upd('cpu_threshold', Number(e.target.value))} />
                        </div>
                        <div className="form-group">
                            <label>RAM, %</label>
                            <input type="number" value={st.ram_threshold}
                                   onChange={e => upd('ram_threshold', Number(e.target.value))} />
                        </div>
                        <div className="form-group">
                            <label>Диск — предупреждение, %</label>
                            <input type="number" value={st.disk_warning}
                                   onChange={e => upd('disk_warning', Number(e.target.value))} />
                        </div>
                        <div className="form-group">
                            <label>Диск — критично, %</label>
                            <input type="number" value={st.disk_critical}
                                   onChange={e => upd('disk_critical', Number(e.target.value))} />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Недоступен более, мин</label>
                            <input type="number" value={st.down_minutes}
                                   onChange={e => upd('down_minutes', Number(e.target.value))} />
                        </div>
                        <div className="form-group">
                            <label>Повторять алерт каждые, мин</label>
                            <input type="number" value={st.repeat_minutes}
                                   onChange={e => upd('repeat_minutes', Number(e.target.value))} />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
